<div class="qps-wrapper" (click)="closeAllDropdowns($event)">
  <div class="qps-card">

    <!-- Progress Bar Header -->
    <div class="qps-header">
      <div class="qps-header-top">
        <h2 class="qps-title">Quick profile setup</h2>
        <span class="qps-step-label">You can edit any detail later.</span>
      </div>
      <div class="qps-progress-bar">
        <div class="qps-progress-fill" [style.width.%]="progressPercent"></div>
      </div>
      <span class="qps-percent">{{ progressPercent }}%</span>
    </div>
    <form [formGroup]="userProfileForm" (ngSubmit)="onSubmit()">

      <!-- ── Section 1: Career Status ─────────────────────── -->
      <div class="qps-section">
        <div class="qps-section-header">
          <div class="qps-section-icon qps-icon-purple">
            <i class="bi bi-briefcase"></i>
          </div>
          <div>
            <h3 class="qps-section-title">Career status</h3>
            <p class="qps-section-sub">This defines your recommendations and can be changed later.</p>
          </div>
        </div>
        <div class="qps-career-options">
          <div class="qps-career-card" [class.selected]="selectedCareer === CareerStatus.FRESHER"
            [style.opacity]="(isCareerStatusLocked && selectedCareer !== CareerStatus.FRESHER) ? '0.5' : '1'"
            [style.pointer-events]="isCareerStatusLocked ? 'none' : 'auto'"
            (click)="selectCareer(CareerStatus.FRESHER)">
            <span class="career-title">I am a Fresher</span>
            <span class="career-desc">Recently graduated or final year student</span>
          </div>
          <div class="qps-career-card" [class.selected]="selectedCareer === CareerStatus.WORKING"
            [style.opacity]="(isCareerStatusLocked && selectedCareer !== CareerStatus.WORKING) ? '0.5' : '1'"
            [style.pointer-events]="isCareerStatusLocked ? 'none' : 'auto'"
            (click)="selectCareer(CareerStatus.WORKING)">
            <span class="career-title">I am a Working Professional</span>
            <span class="career-desc">I have work experience</span>
          </div>
        </div>
      </div>

      <!-- ── Section 2: Personal Details ─────────────────── -->
      <div class="qps-section">
        <div class="qps-section-header">
          <div class="qps-section-icon qps-icon-orange">
            <i class="bi bi-person"></i>
          </div>
          <div>
            <h3 class="qps-section-title">Personal details</h3>
            <p class="qps-section-sub">These details will help us better understand you.</p>
          </div>
        </div>

        <div class="qps-form-grid">
          <div class="qps-form-field" [class.missing-highlight]="highlightedFields.includes('first_name')">
            <label>First name <span class="req">*</span></label>
            <input type="text" formControlName="first_name" placeholder="e.g. Anand" />
            <div
              *ngIf="userProfileForm.get('first_name')?.invalid && (userProfileForm.get('first_name')?.dirty || userProfileForm.get('first_name')?.touched)"
              class="text-danger mt-1" style="font-size: 12px;">
              <div *ngIf="userProfileForm.get('first_name')?.errors?.['required']">First name is required.</div>
              <div *ngIf="userProfileForm.get('first_name')?.errors?.['minlength']">Must be at least 2 characters.</div>
              <div *ngIf="userProfileForm.get('first_name')?.errors?.['maxlength']">Must be at most 50 characters.</div>
              <div *ngIf="userProfileForm.get('first_name')?.errors?.['pattern']">Enter a valid name.</div>
            </div>
          </div>

          <div class="qps-form-field">
            <label>Last name</label>
            <input type="text" formControlName="last_name" placeholder="e.g. Sharma" />
            <div
              *ngIf="userProfileForm.get('last_name')?.invalid && (userProfileForm.get('last_name')?.dirty || userProfileForm.get('last_name')?.touched)"
              class="text-danger mt-1" style="font-size: 12px;">
              <div *ngIf="userProfileForm.get('last_name')?.errors?.['minlength']">Must be at least 1 character.</div>
              <div *ngIf="userProfileForm.get('last_name')?.errors?.['maxlength']">Must be at most 50 characters.</div>
              <div *ngIf="userProfileForm.get('last_name')?.errors?.['pattern']">Enter a valid name.</div>
            </div>
          </div>

          <!-- Mobile number – Update button sits inside the input -->
          <div class="qps-form-field" [class.missing-highlight]="highlightedFields.includes('phone_number')">
            <label>Mobile number <span class="req">*</span></label>
            <div class="input-action-wrap">
              <input type="text" formControlName="phone_number" placeholder="+91 9876543210" readonly
                class="has-action-btn" />
              <button type="button" class="btn-inline-action" (click)="openPhoneUpdateModal()">
                <i class="bi bi-pencil-square"></i> Update
              </button>
            </div>
          </div>

          <!-- Email address – Update button sits inside the input -->
          <div class="qps-form-field" [class.missing-highlight]="highlightedFields.includes('phone_email')">
            <label>Email address <span class="req">*</span></label>
            <div class="input-action-wrap">
              <input type="email" formControlName="phone_email" placeholder="you@example.com" readonly
                class="has-action-btn" />
              <button type="button" class="btn-inline-action" (click)="openEmailUpdateModal()">
                <i class="bi bi-pencil-square"></i> Update
              </button>
            </div>
          </div>

          <div class="qps-form-field" [class.missing-highlight]="highlightedFields.includes('city')">
            <label>Current location / city <span class="req">*</span></label>
            <input type="text" formControlName="city" placeholder="e.g. Pune" />
            <div
              *ngIf="userProfileForm.get('city')?.invalid && (userProfileForm.get('city')?.dirty || userProfileForm.get('city')?.touched)"
              class="text-danger mt-1" style="font-size: 12px;">
              <div *ngIf="userProfileForm.get('city')?.errors?.['required']">City is required.</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Section 3: Education ─────────────────────────── -->
      <div class="qps-section">
        <div class="qps-section-header">
          <div class="qps-section-icon qps-icon-blue">
            <i class="bi bi-mortarboard"></i>
          </div>
          <div>
            <h3 class="qps-section-title">Education</h3>
          </div>
        </div>

        <div class="qps-form-grid">
          <!-- Highest Education custom dropdown -->
          <div class="qps-form-field" [class.missing-highlight]="highlightedFields.includes('education')">
            <label>Highest education <span class="req">*</span></label>
            <div class="cdd-wrap" [class.open]="dropdowns['education'].open" (click)="$event.stopPropagation()">
              <div class="cdd-trigger" (click)="toggleDropdown('education')">
                <span [class.cdd-placeholder]="!dropdowns['education'].selected">
                  {{ dropdowns['education'].selected || 'Select' }}
                </span>
                <i class="bi bi-chevron-down cdd-arrow"></i>
              </div>
              <div class="cdd-panel" *ngIf="dropdowns['education'].open">
                <div class="cdd-search">
                  <i class="bi bi-search"></i>
                  <input type="text" [(ngModel)]="dropdowns['education'].search" [ngModelOptions]="{standalone:true}"
                    placeholder="Search..." (click)="$event.stopPropagation()" />
                </div>
                <ul class="cdd-list">
                  <li *ngFor="let opt of filterOptions(educationOptions, dropdowns['education'].search)"
                    (click)="selectOption('education', opt)">
                    {{ opt }}
                  </li>
                  <li class="cdd-empty"
                    *ngIf="filterOptions(educationOptions, dropdowns['education'].search).length === 0">
                    No results found
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="qps-form-field" [class.missing-highlight]="highlightedFields.includes('college_name')">
            <label>College / university <span class="req">*</span></label>
            <app-college-autocomplete class="profile-college-ac" [control]="collegeNameControl" [minSearchLength]="5"
              (collegeSelected)="selectInstitution($event)" (selectionCleared)="onCollegeSelectionCleared()"
              (cantFind)="onCantFindCollege()">
              <input type="text" formControlName="college_name" placeholder="Search your college/university..."
                autocomplete="off" maxlength="255" />
              <div
                *ngIf="userProfileForm.get('college_name')?.invalid && (userProfileForm.get('college_name')?.dirty || userProfileForm.get('college_name')?.touched)"
                class="text-danger mt-1" style="font-size: 12px;">
                <div *ngIf="userProfileForm.get('college_name')?.errors?.['required']">College/University name is required.</div>
              </div>
              <div *ngIf="userProfileForm.get('college_name')?.valid && isCollegeSelectionRequired"
                class="text-danger mt-1" style="font-size: 12px;">
                Type at least 5 characters and select your college from the list, or use "Can't find your college?" to add it.
              </div>
            </app-college-autocomplete>
          </div>

          <!-- Year of passing custom dropdown -->
          <div class="qps-form-field" [class.missing-highlight]="highlightedFields.includes('year')">
            <label>Year of passing <span class="req" *ngIf="selectedCareer === CareerStatus.FRESHER">*</span></label>
            <div class="cdd-wrap" [class.open]="dropdowns['year'].open" (click)="$event.stopPropagation()">
              <div class="cdd-trigger" (click)="toggleDropdown('year')">
                <span [class.cdd-placeholder]="!dropdowns['year'].selected">
                  {{ dropdowns['year'].selected || 'Select year' }}
                </span>
                <i class="bi bi-chevron-down cdd-arrow"></i>
              </div>
              <div class="cdd-panel" *ngIf="dropdowns['year'].open">
                <div class="cdd-search">
                  <i class="bi bi-search"></i>
                  <input type="text" [(ngModel)]="dropdowns['year'].search" [ngModelOptions]="{standalone:true}"
                    placeholder="Search year..." (click)="$event.stopPropagation()" />
                </div>
                <ul class="cdd-list">
                  <li *ngFor="let y of filterOptions(yearStringOptions, dropdowns['year'].search)"
                    (click)="selectOption('year', y)">{{ y }}</li>
                  <li class="cdd-empty" *ngIf="filterOptions(yearStringOptions, dropdowns['year'].search).length === 0">
                    No results found
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Section 4: Professional Details (Working Professional only) -->
      <div class="qps-section qps-animated-section" *ngIf="selectedCareer === CareerStatus.WORKING">
        <div class="qps-section-header">
          <div class="qps-section-icon qps-icon-indigo">
            <i class="bi bi-building"></i>
          </div>
          <div>
            <h3 class="qps-section-title">Professional details</h3>
            <p class="qps-section-sub">Helps us tailor opportunities to your experience level.</p>
          </div>
        </div>

        <div class="qps-form-grid">
          <div class="qps-form-field" [class.missing-highlight]="highlightedFields.includes('company_title')">
            <label>Current Company title <span class="req">*</span></label>
            <input type="text" [(ngModel)]="professionalDetails.companyTitle" [ngModelOptions]="{standalone:true}"
              (ngModelChange)="removeHighlight('company_title')" placeholder="e.g. Infosys, TCS" />
          </div>
          <div class="qps-form-field" [class.missing-highlight]="highlightedFields.includes('current_role')">
            <label>Current role / Designation <span class="req">*</span></label>
            <input type="text" [(ngModel)]="professionalDetails.currentRole" [ngModelOptions]="{standalone:true}"
              (ngModelChange)="removeHighlight('current_role')" placeholder="e.g. Senior Developer" />
          </div>

          <!-- Years of experience custom dropdown -->
          <div class="qps-form-field" [class.missing-highlight]="highlightedFields.includes('experience')">
            <label>Years of experience <span class="req">*</span></label>
            <div class="cdd-wrap" [class.open]="dropdowns['experience'].open" (click)="$event.stopPropagation()">
              <div class="cdd-trigger" (click)="toggleDropdown('experience')">
                <span [class.cdd-placeholder]="!dropdowns['experience'].selected">
                  {{ dropdowns['experience'].selected || 'Select' }}
                </span>
                <i class="bi bi-chevron-down cdd-arrow"></i>
              </div>
              <div class="cdd-panel" *ngIf="dropdowns['experience'].open">
                <div class="cdd-search">
                  <i class="bi bi-search"></i>
                  <input type="text" [(ngModel)]="dropdowns['experience'].search" [ngModelOptions]="{standalone:true}"
                    placeholder="Search..." (click)="$event.stopPropagation()" />
                </div>
                <ul class="cdd-list">
                  <li *ngFor="let opt of filterOptions(experienceOptions, dropdowns['experience'].search)"
                    (click)="selectOption('experience', opt)">{{ opt }}</li>
                  <li class="cdd-empty"
                    *ngIf="filterOptions(experienceOptions, dropdowns['experience'].search).length === 0">
                    No results found</li>
                </ul>
              </div>
            </div>
          </div>

          <div class="qps-form-field">
            <label>e.g. React-3, TypeScript-3 years / month / URL links</label>
            <input type="text" [(ngModel)]="professionalDetails.techStack" [ngModelOptions]="{standalone:true}"
              placeholder="e.g. React-3, TypeScript-3 years/month URL-links" />
          </div>

          <div class="qps-form-field">
            <label>Current CTC (LPA)</label>
            <input type="text" [(ngModel)]="professionalDetails.currentCtc" [ngModelOptions]="{standalone:true}"
              placeholder="e.g. 12" />
          </div>
          <div class="qps-form-field">
            <label>Expected CTC (LPA)</label>
            <input type="text" [(ngModel)]="professionalDetails.expectedCtc" [ngModelOptions]="{standalone:true}"
              placeholder="e.g. 18" />
          </div>
        </div>

        <!-- Preferred job search locations -->
        <div class="qps-form-field mt-3">
          <label>Preferred job search locations</label>
        </div>
        <div class="qps-form-grid mt-1">
          <div class="qps-form-field">
            <label class="sub-label">City preferred</label>
            <input type="text" [(ngModel)]="professionalDetails.city1" [ngModelOptions]="{standalone:true}"
              placeholder="e.g. Bengaluru" />
          </div>
          <div class="qps-form-field">
            <label class="sub-label">City preferred - II</label>
            <input type="text" [(ngModel)]="professionalDetails.city2" [ngModelOptions]="{standalone:true}"
              placeholder="e.g. Pune" />
          </div>
          <div class="qps-form-field">
            <label class="sub-label">City preferred - III</label>
            <input type="text" [(ngModel)]="professionalDetails.city3" [ngModelOptions]="{standalone:true}"
              placeholder="e.g. Hyderabad" />
          </div>
        </div>
      </div>

      <!-- ── Section 5: Career Preferences ───────────────── -->
      <div class="qps-section">
        <div class="qps-section-header">
          <div class="qps-section-icon qps-icon-teal">
            <i class="bi bi-compass"></i>
          </div>
          <div>
            <h3 class="qps-section-title">Career preferences</h3>
            <p class="qps-section-sub">Used to tailor job recommendations for you.</p>
          </div>
        </div>

        <div class="qps-form-grid">
          <!-- Preferred job role category — custom dropdown -->
          <div class="qps-form-field">
            <label>Preferred job role category</label>
            <div class="cdd-wrap" [class.open]="dropdowns['jobCategory'].open" (click)="$event.stopPropagation()">
              <div class="cdd-trigger" (click)="toggleDropdown('jobCategory')">
                <span [class.cdd-placeholder]="!dropdowns['jobCategory'].selected">
                  {{ dropdowns['jobCategory'].selected || 'Select category' }}
                </span>
                <i class="bi bi-chevron-down cdd-arrow"></i>
              </div>
              <div class="cdd-panel" *ngIf="dropdowns['jobCategory'].open">
                <div class="cdd-search">
                  <i class="bi bi-search"></i>
                  <input type="text" [(ngModel)]="dropdowns['jobCategory'].search" [ngModelOptions]="{standalone:true}"
                    placeholder="Search category..." (click)="$event.stopPropagation()" />
                </div>
                <ul class="cdd-list">
                  <li *ngFor="let opt of filterOptions(jobCategoryOptions, dropdowns['jobCategory'].search)"
                    (click)="selectOption('jobCategory', opt)">{{ opt }}</li>
                  <li class="cdd-other-option"
                    *ngIf="filterOptions(['Other'], dropdowns['jobCategory'].search).length > 0"
                    (click)="selectOption('jobCategory', 'Other')">
                    <i class="bi bi-plus-circle me-2"></i> Other (enter your own)
                  </li>
                  <li class="cdd-empty" *ngIf="filterOptions(jobCategoryOptions, dropdowns['jobCategory'].search).length === 0
                      && filterOptions(['Other'], dropdowns['jobCategory'].search).length === 0">
                    No results found</li>
                </ul>
              </div>
            </div>
            <!-- Free-text input when 'Other' is selected -->
            <div class="cdd-other-input" *ngIf="dropdowns['jobCategory'].selected === 'Other'">
              <i class="bi bi-pencil"></i>
              <input type="text" [(ngModel)]="customJobCategory" [ngModelOptions]="{standalone:true}"
                placeholder="Type your job category…" autofocus />
            </div>
          </div>

          <div class="qps-form-field">
            <label>Preferred job titles (optional, add multiple)</label>
            <div class="qps-skills-input-row">
              <input type="text" [(ngModel)]="jobTitleInput" [ngModelOptions]="{standalone:true}"
                placeholder="e.g. Frontend Developer" (keydown.enter)="addJobTitle($event)" />
              <button type="button" class="qps-add-btn" (click)="addJobTitle()">
                <i class="bi bi-plus"></i> Add
              </button>
            </div>
          </div>
        </div>

        <div class="qps-skills-tags mt-2" *ngIf="preferredJobTitles.length > 0">
          <span class="qps-skill-tag" *ngFor="let title of preferredJobTitles; let i = index">
            {{ title }}
            <button type="button" class="tag-remove" (click)="removeJobTitle(i)">
              <i class="bi bi-x"></i>
            </button>
          </span>
        </div>
      </div>

      <!-- ── Section 6: Skills ────────────────────────────── -->
      <div class="qps-section" [class.missing-highlight]="highlightedFields.includes('skills')">
        <div class="qps-section-header">
          <div class="qps-section-icon qps-icon-green">
            <i class="bi bi-stars"></i>
          </div>
          <div>
            <h3 class="qps-section-title">Skills</h3>
            <p class="qps-section-sub">Add at least 3 skills. Press Enter to add each.</p>
          </div>
        </div>

        <div class="qps-skills-input-row">
          <input type="text" [(ngModel)]="skillInput" [ngModelOptions]="{standalone: true}"
            placeholder="Type and press Enter / Add" (keydown.enter)="addSkill($event)" />
          <button type="button" class="qps-add-btn" (click)="addSkill()">
            <i class="bi bi-plus"></i> Add
          </button>
        </div>

        <div class="qps-skills-tags" *ngIf="skills.length > 0">
          <span class="qps-skill-tag" *ngFor="let skill of skills; let i = index">
            {{ skill }}
            <button type="button" class="tag-remove" (click)="removeSkill(i)">
              <i class="bi bi-x"></i>
            </button>
          </span>
        </div>

        <div class="qps-skill-suggestions" *ngIf="suggestedSkills.length > 0">
          <span class="suggest-label">Suggested:</span>
          <button type="button" class="qps-suggest-chip" *ngFor="let s of suggestedSkills"
            (click)="addSuggestedSkill(s)">
            + {{ s }}
          </button>
        </div>
      </div>

      <!-- ── Section 7: Resume ────────────────────────────── -->
      <div class="qps-section">
        <div class="qps-section-header">
          <div class="qps-section-icon qps-icon-red">
            <i class="bi bi-file-earmark-text"></i>
          </div>
          <div>
            <h3 class="qps-section-title">Resume</h3>
            <p class="qps-section-sub">PDF, DOC or DOCX — up to 10 MB</p>
          </div>
        </div>

        <div class="qps-upload-zone" (click)="fileInput.click()" (dragover)="$event.preventDefault()"
          (drop)="onFileDrop($event)" [class.has-file]="resumeFile || existingResumeUrl"
          [class.missing-highlight]="highlightedFields.includes('resume')">
          <input #fileInput type="file" accept=".pdf,.doc,.docx" hidden (change)="onFileChange($event)" />
          <div *ngIf="!resumeFile && !existingResumeUrl; else filePreview" class="qps-upload-placeholder">
            <i class="bi bi-cloud-upload"></i>
            <p>Drag &amp; drop or <span class="upload-link">click to upload</span></p>
            <small>Accepted: PDF, DOC, DOCX — max 10 MB</small>
          </div>
          <ng-template #filePreview>
            <div class="qps-file-preview">
              <i class="bi bi-file-earmark-check"></i>
              <span *ngIf="resumeFile">{{ resumeFile.name }}</span>
              <span *ngIf="!resumeFile && existingResumeUrl">
                <a [href]="existingResumeUrl" target="_blank" (click)="$event.stopPropagation()">{{ existingResumeName
                  }}</a>
              </span>
              <button *ngIf="resumeFile" type="button" class="remove-file ms-3" title="Remove"
                (click)="removeResume($event)">
                <i class="bi bi-x-circle"></i>
              </button>
              <button *ngIf="!resumeFile && existingResumeUrl" type="button" class="replace-file-btn ms-3"
                title="Replace" (click)="fileInput.click(); $event.stopPropagation()">
                <i class="bi bi-arrow-repeat"></i> Replace
              </button>
            </div>
          </ng-template>
        </div>

        <div class="saved-resumes-container" *ngIf="savedResumes.length > 0">
          <div class="saved-resumes-title">
            <i class="bi bi-folder-check"></i>
            <span>Or select one of your saved resumes:</span>
          </div>
          <div class="saved-resumes-list">
            <div class="saved-resume-item" *ngFor="let resume of savedResumes" (click)="selectSavedResume(resume.resume_id)">
              <div class="resume-icon">
                <i class="bi bi-file-earmark-text"></i>
              </div>
              <div class="resume-info">
                <span class="resume-name">{{ resume.resume_name || 'Untitled Resume' }}</span>
                <span class="resume-date">Updated on {{ resume.updated_at | date:'mediumDate' }}</span>
              </div>
              <div class="select-action">
                <span>Select & View <i class="bi bi-chevron-right"></i></span>
              </div>
            </div>
          </div>
        </div>

        <div class="qps-resume-footer">
          <span class="dont-have-resume">Don't have a resume?</span>
          <button type="button" class="qps-build-link" (click)="goToResumeBuilderPage()">Build one <i
              class="bi bi-arrow-right"></i></button>
        </div>

        <div class="qps-form-field mt-3">
          <label>LinkedIn URL (optional)</label>
          <input type="url" [(ngModel)]="linkedInUrl" [ngModelOptions]="{standalone:true}"
            placeholder="https://linkedin.com/in/" />
        </div>
      </div>

      <!-- ── Footer Actions ───────────────────────────────── -->
      <div class="qps-footer-actions" *hasPermission="PermissionForStudents.USER_UPDATE">
        <button type="button" class="qps-btn-cancel" (click)="cancel()">Cancel</button>
        <button type="submit" class="qps-btn-save" [disabled]="isLoading || !hasChanges">
          <ng-container *ngIf="isLoading; else saveLbl">
            <span class="spinner-border spinner-border-sm me-2"></span> Saving...
          </ng-container>
          <ng-template #saveLbl>Save changes</ng-template>
        </button>
      </div>

    </form>
  </div>
</div>

<!-- Mobile Update Modal -->
<div #phoneModal class="custom-modal-overlay" [style.display]="showPhoneUpdateModal ? 'flex' : 'none'"
  (click)="closePhoneUpdateModal()">
  <div class="custom-modal-content" (click)="$event.stopPropagation()">
    <button type="button" class="modal-close-btn" (click)="closePhoneUpdateModal()">
      <i class="bi bi-x"></i>
    </button>
    <div class="modal-icon purple-icon">
      <i class="bi bi-telephone"></i>
    </div>
    <h3 class="modal-title">Update mobile number</h3>
    <p class="modal-subtitle">For your security, we'll send a 6-digit OTP to your new number to verify it before
      updating.</p>

    <div class="modal-form-group">
      <label>Current mobile number</label>
      <input type="text" [value]="userProfileForm.get('phone_number')?.value" readonly
        class="modal-input readonly-input" />
    </div>

    <div class="modal-form-group" *ngIf="!phoneUpdateToken">
      <label>New mobile number</label>
      <input type="tel" [(ngModel)]="newPhoneInput" [ngModelOptions]="{standalone:true}"
        placeholder="Enter 10-digit number" class="modal-input" maxlength="10" (keypress)="allowOnlyNumbers($event)"
        (keydown.enter)="requestPhoneOtp()" />
      <p class="error-text" *ngIf="phoneUpdateError">{{ phoneUpdateError }}</p>
    </div>

    <div class="modal-form-group" *ngIf="phoneUpdateToken">
      <label>Enter 6-digit OTP</label>
      <input type="text" [(ngModel)]="phoneOtpInput" [ngModelOptions]="{standalone:true}"
        placeholder="Enter 6-digit OTP" class="modal-input" maxlength="6" (keypress)="allowOnlyNumbers($event)"
        (keydown.enter)="verifyPhoneOtp()" />
      <p class="error-text" *ngIf="phoneUpdateError">{{ phoneUpdateError }}</p>
    </div>

    <div class="modal-actions">
      <button type="button" class="btn-modal-cancel" (click)="closePhoneUpdateModal()">Cancel</button>
      <button type="button" class="btn-modal-primary" *ngIf="!phoneUpdateToken" (click)="requestPhoneOtp()"
        [disabled]="isPhoneOtpLoading">
        <ng-container *ngIf="isPhoneOtpLoading; else sendOtpLbl">
          <span class="spinner-border spinner-border-sm me-2"></span> Sending...
        </ng-container>
        <ng-template #sendOtpLbl>Send OTP <i class="bi bi-arrow-right ms-2"></i></ng-template>
      </button>
      <button type="button" class="btn-modal-primary" *ngIf="phoneUpdateToken" (click)="verifyPhoneOtp()"
        [disabled]="isPhoneOtpLoading">
        <ng-container *ngIf="isPhoneOtpLoading; else verifyOtpLbl">
          <span class="spinner-border spinner-border-sm me-2"></span> Verifying...
        </ng-container>
        <ng-template #verifyOtpLbl>Verify & Update</ng-template>
      </button>
    </div>

    <div class="modal-resend" *ngIf="phoneUpdateToken">
      <p class="resend-text">Didn't receive the OTP?
        <button type="button" class="resend-btn" [disabled]="phoneOtpTimer > 0 || isPhoneOtpLoading"
          (click)="resendPhoneOtp()">
          Resend
        </button>
        <span *ngIf="phoneOtpTimer > 0" class="timer-text"> in {{ phoneOtpTimer }}s</span>
      </p>
    </div>
  </div>
</div>

<!-- Email Update Modal -->
<div #emailModal class="custom-modal-overlay" [style.display]="showEmailUpdateModal ? 'flex' : 'none'"
  (click)="closeEmailUpdateModal()">
  <div class="custom-modal-content" (click)="$event.stopPropagation()">
    <button type="button" class="modal-close-btn" (click)="closeEmailUpdateModal()">
      <i class="bi bi-x"></i>
    </button>
    <div class="modal-icon purple-icon">
      <i class="bi bi-envelope"></i>
    </div>
    <h3 class="modal-title">Update email address</h3>
    <p class="modal-subtitle">For your security, we'll send a 6-digit OTP to your new email to verify it before
      updating.</p>

    <div class="modal-form-group">
      <label>Current email address</label>
      <input type="email" [value]="userProfileForm.get('phone_email')?.value" readonly
        class="modal-input readonly-input" />
    </div>

    <div class="modal-form-group" *ngIf="!emailUpdateToken">
      <label>New email address</label>
      <input type="email" [(ngModel)]="newEmailInput" [ngModelOptions]="{standalone:true}" placeholder="you@example.com"
        class="modal-input" (keydown.enter)="requestEmailOtp()" />
      <p class="error-text" *ngIf="emailUpdateError">{{ emailUpdateError }}</p>
    </div>

    <div class="modal-form-group" *ngIf="emailUpdateToken">
      <label>Enter 6-digit OTP</label>
      <input type="text" [(ngModel)]="emailOtpInput" [ngModelOptions]="{standalone:true}"
        placeholder="Enter 6-digit OTP" class="modal-input" maxlength="6" (keypress)="allowOnlyNumbers($event)"
        (keydown.enter)="verifyEmailOtp()" />
      <p class="error-text" *ngIf="emailUpdateError">{{ emailUpdateError }}</p>
    </div>

    <div class="modal-actions">
      <button type="button" class="btn-modal-cancel" (click)="closeEmailUpdateModal()">Cancel</button>
      <button type="button" class="btn-modal-primary" *ngIf="!emailUpdateToken" (click)="requestEmailOtp()"
        [disabled]="isEmailOtpLoading">
        <ng-container *ngIf="isEmailOtpLoading; else sendEmailOtpLbl">
          <span class="spinner-border spinner-border-sm me-2"></span> Sending...
        </ng-container>
        <ng-template #sendEmailOtpLbl>Send OTP <i class="bi bi-arrow-right ms-2"></i></ng-template>
      </button>
      <button type="button" class="btn-modal-primary" *ngIf="emailUpdateToken" (click)="verifyEmailOtp()"
        [disabled]="isEmailOtpLoading">
        <ng-container *ngIf="isEmailOtpLoading; else verifyEmailOtpLbl">
          <span class="spinner-border spinner-border-sm me-2"></span> Verifying...
        </ng-container>
        <ng-template #verifyEmailOtpLbl>Verify & Update</ng-template>
      </button>
    </div>

    <div class="modal-resend" *ngIf="emailUpdateToken">
      <p class="resend-text">Didn't receive the OTP?
        <button type="button" class="resend-btn" [disabled]="emailOtpTimer > 0 || isEmailOtpLoading"
          (click)="resendEmailOtp()">
          Resend
        </button>
        <span *ngIf="emailOtpTimer > 0" class="timer-text"> in {{ emailOtpTimer }}s</span>
      </p>
    </div>
  </div>
</div>